import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  RotateCcw, 
  Flag, 
  Handshake, 
  Play, 
  Pause,
  ChevronLeft,
  ChevronRight,
  SkipBack,
  SkipForward
} from "lucide-react";

interface GameControlsProps {
  onTakeback?: () => void;
  onResign?: () => void;
  onOfferDraw?: () => void;
  onPause?: () => void;
  canTakeback?: boolean;
  canPause?: boolean;
  isPaused?: boolean;
  showMoveNavigation?: boolean;
  onFirstMove?: () => void;
  onPreviousMove?: () => void;
  onNextMove?: () => void;
  onLastMove?: () => void;
  canGoBack?: boolean;
  canGoForward?: boolean;
  compact?: boolean;
}

export function GameControls({
  onTakeback,
  onResign,
  onOfferDraw,
  onPause,
  canTakeback = false,
  canPause = false,
  isPaused = false,
  showMoveNavigation = false,
  onFirstMove,
  onPreviousMove,
  onNextMove,
  onLastMove,
  canGoBack = false,
  canGoForward = false,
  compact = false
}: GameControlsProps) {
  if (compact) {
    return (
      <Card className="border-accent/20">
        <CardContent className="p-2">
          <div className="flex flex-wrap gap-1 justify-center">
            {onTakeback && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onTakeback}
                disabled={!canTakeback}
                className="flex items-center gap-1 text-xs px-2 py-1 h-7"
              >
                <RotateCcw className="h-3 w-3" />
                Takeback
              </Button>
            )}
            
            {onOfferDraw && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onOfferDraw}
                className="flex items-center gap-1 text-xs px-2 py-1 h-7"
              >
                <Handshake className="h-3 w-3" />
                Draw
              </Button>
            )}
            
            {onResign && (
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={onResign}
                className="flex items-center gap-1 text-xs px-2 py-1 h-7"
              >
                <Flag className="h-3 w-3" />
                Resign
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-accent/20">
      <CardContent className="p-4">
        <div className="flex flex-col gap-4">
          {/* Game Actions */}
          <div className="flex items-center justify-center gap-2">
            {onTakeback && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onTakeback}
                disabled={!canTakeback}
                className="flex items-center gap-2"
              >
                <RotateCcw className="h-4 w-4" />
                Takeback
              </Button>
            )}
            
            {onOfferDraw && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onOfferDraw}
                className="flex items-center gap-2"
              >
                <Handshake className="h-4 w-4" />
                Draw
              </Button>
            )}
            
            {onPause && canPause && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onPause}
                className="flex items-center gap-2"
              >
                {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                {isPaused ? 'Resume' : 'Pause'}
              </Button>
            )}
            
            {onResign && (
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={onResign}
                className="flex items-center gap-2"
              >
                <Flag className="h-4 w-4" />
                Resign
              </Button>
            )}
          </div>

          {/* Move Navigation */}
          {showMoveNavigation && (
            <>
              <Separator />
              <div className="flex items-center justify-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onFirstMove}
                  disabled={!canGoBack}
                  className="flex items-center gap-1"
                >
                  <SkipBack className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onPreviousMove}
                  disabled={!canGoBack}
                  className="flex items-center gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onNextMove}
                  disabled={!canGoForward}
                  className="flex items-center gap-1"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onLastMove}
                  disabled={!canGoForward}
                  className="flex items-center gap-1"
                >
                  <SkipForward className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}